import React, { useEffect, useState } from 'react'
import { GlobalNav } from '~/components/GlobalNav'
import { Link, useNavigate } from 'react-router-dom'
import { shortAddress } from '~/utils/shortAddress'
import { extractGithubRepositoryName } from '~/utils/github'
import { api } from '~/services/api'

type APIGame = {
  dagId: string;
  dagName: string;
  tokenAddress: string;
  githubRepositoryUrl: string;
  createdAt: string;
}

type GameData = {
  id: string;
  game: {
    id: string;
    name: string;
    category: string;
    categoryColor: string;
    img: string;
  };
  token: {
    address: string;
    symbol: string;
    img: string;
    marketCap: string;
    transactions: string;
    volume: {
      total: string;
      '5m': string;
      '1h': string;
      '6h': string;
      '24h': string;
    };
  };
  github: {
    pr: string;
    commits: string;
    stars: string;
    link: string;
    repo: string | null;
  };
}

export default function Home() {
  const [games, setGames] = useState<Record<string, GameData>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const gamesData = await api.games.getAll()
        // Convert array to record/object format expected by components
        const gamesRecord = gamesData.reduce((acc, game) => ({
          ...acc,
          [game.dagId]: {
            id: game.dagId,
            game: {
              id: game.dagId,
              name: game.dagName,
              category: 'Game', // Default category since not provided by API
              categoryColor: '#60e25e', // Default color since not provided by API
              img: `https://picsum.photos/seed/${game.dagId}/800/400`, // Placeholder image
            },
            token: {
              address: game.tokenAddress,
              symbol: shortAddress(game.tokenAddress),
              img: `https://picsum.photos/seed/${game.dagId}/200/200`, // Placeholder image
              marketCap: '0', // Not provided by API
              transactions: '0', // Not provided by API
              volume: {
                total: '0',
                '5m': '0',
                '1h': '0',
                '6h': '0',
                '24h': '0',
              },
            },
            github: {
              pr: '0', // Not provided by API
              commits: '0', // Not provided by API
              stars: '0', // Not provided by API
              link: game.githubRepositoryUrl,
              repo: extractGithubRepositoryName(game.githubRepositoryUrl),
            },
          },
        }), {} as Record<string, GameData>)
        
        setGames(gamesRecord)
      } catch (error) {
        console.error('Failed to fetch games:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGames()
  }, [])

  if (loading) {
    return (
      <>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-white">Loading...</p>
        </div>
      </>
    )
  }

  return (
    <>
      <GlobalNav />
      <Hero games={games} />
      <Table games={games} />
    </>
  )
}

const Hero = ({ games }: { games: Record<string, GameData> }) => {
  // Get only the first 3 games for hero section
  const featuredGames = Object.values(games).slice(0, 3)
  
  return (
    <div className='flex gap-6 px-10 py-8'>
      {featuredGames.map((item) => (
        <Link 
          key={item.id}
          to={`/games/${item.github.repo || item.id}`} 
          className='flex-1 relative rounded-lg overflow-hidden h-[296px] border border-solid border-white/10'
        >
          <img className='absolute inset-0 w-full h-full object-cover' src={item.game.img} alt={item.game.name} />

          <div className='relative flex p-4'>
            <div
              className="px-3 py-2 rounded-[100px] bg-[#212425] border border-solid border-white/10"
              style={{ boxShadow: "0px 0px 4px 0 rgba(0,0,0,0.15)" }}
            >
              <p className="text-xs font-semibold text-left text-[#ff4949]" style={{ color: item.game.categoryColor }}>
                {item.game.category}
              </p>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className='absolute top-0 left-0 w-full h-full' style={{ background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85)), rgba(0, 0, 0, 0.5))' }} />

            <div className='flex flex-col gap-2'>
              <p className="text-base font-semibold text-left text-white">
                {item.game.name}
              </p>
              <div className="flex items-start relative gap-2">
                <p className="text-[13px] text-left text-[#9c9e9e]">Volume</p>
                <p className="text-[13px] font-semibold text-left text-white">
                  {item.token.volume.total}
                </p>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}

const Table = ({ games }: { games: Record<string, GameData> }) => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<'trending' | 'top'>('trending')
  const gamesArray = Object.values(games)

  return (
    <>
      <div className="flex gap-6 px-10 py-6">
        <button className="text-xl font-semibold" style={{ color: activeTab === 'trending' ? '#60e25e' : '#6b6d6e' }} onClick={() => setActiveTab('trending')}>Trending</button>
        <button className="text-xl font-semibold" style={{ color: activeTab === 'top' ? '#60e25e' : '#6b6d6e' }} onClick={() => setActiveTab('top')}>Top</button>
      </div>

      <div className='px-10'>
        <table className="table-auto w-full">
          <thead>
            <tr className="border-b border-collapse border-white/10 border-solid text-xs font-medium text-[#6b6d6e]">
              <th className="py-3.5 align-middle text-left">Project Name</th>
              <th className="py-3.5 align-middle">Market Cap</th>
              <th className="py-3.5 align-middle">Txns</th>
              <th className="py-3.5 align-middle">Volume</th>
              <th className="py-3.5 align-middle">5m</th>
              <th className="py-3.5 align-middle">1H</th>
              <th className="py-3.5 align-middle">6H</th>
              <th className="py-3.5 align-middle">24H</th>
              <th className="py-3.5 align-middle">Github Score</th>
            </tr>
          </thead>
          <tbody>
            {gamesArray.map((item) => (
              <tr
                key={item.game.id}
                className="text-sm font-medium text-center text-[#b5b6b6] border-b border-collapse border-white/10 border-solid cursor-pointer"
                onClick={() => navigate(`/games/${item.github.repo || item.id}`)}
              >
                <td className="py-3.5 align-middle">
                  <div className="flex items-center relative gap-4">
                    <img className="w-12 h-12 rounded-lg bg-[#2d3031]" src={item.token.img} />
                    <div className="flex flex-col items-start flex-grow relative">
                      <p className="text-[15px] font-semibold text-left text-[#b5b6b6]">
                        {item.game.name}
                      </p>
                      <div className="flex justify-center items-center relative">
                        <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
                          {item.token.symbol}
                        </p>
                        <div className="flex items-center relative px-1.5">
                          <svg
                            width={2}
                            height={3}
                            viewBox="0 0 2 3"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="flex-grow-0 flex-shrink-0"
                            preserveAspectRatio="xMidYMid meet"
                          >
                            <circle cx={1} cy="1.5" r={1} fill="#525556" />
                          </svg>
                        </div>
                        <div className="flex items-center relative gap-1">
                          <p className="text-[13px] font-medium text-left text-[#525556]">
                            {shortAddress(item.token.address)}
                          </p>
                          <svg
                            width={9}
                            height={10}
                            viewBox="0 0 9 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-2 h-[8.06px]"
                            preserveAspectRatio="none"
                          >
                            <rect x="0.5" y="3.03125" width={6} height={6} rx="0.5" stroke="#525556" />
                            <path d="M2 1.46875H7C7.55228 1.46875 8 1.91647 8 2.46875V6.90625" stroke="#525556" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>

                <td className="py-3.5 align-middle">
                  {item.token.marketCap}
                </td>

                <td className="py-3.5 align-middle">
                  {item.token.transactions}
                </td>

                <td className="py-3.5 align-middle">
                  {item.token.volume.total}
                </td>

                <td className="py-3.5 align-middle" style={{ color: parseFloat(item.token.volume['5m']) > 0 ? '#60e25e' : '#ff4949' }}>
                  {item.token.volume['5m']}
                </td>
                <td className="py-3.5 align-middle" style={{ color: parseFloat(item.token.volume['1h']) > 0 ? '#60e25e' : '#ff4949' }}>
                  {item.token.volume['1h']}
                </td>
                <td className="py-3.5 align-middle" style={{ color: parseFloat(item.token.volume['6h']) > 0 ? '#60e25e' : '#ff4949' }}>
                  {item.token.volume['6h']}
                </td>
                <td className="py-3.5 align-middle" style={{ color: parseFloat(item.token.volume['24h']) > 0 ? '#60e25e' : '#ff4949' }}>
                  {item.token.volume['24h']}
                </td>

                <td className="py-3.5 align-middle">
                  <div className="flex justify-center items-center gap-3">
                    <div className="flex items-center relative gap-1">
                      <svg
                        width={17}
                        height={16}
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 relative"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M4.9444 5.56141C5.92624 5.56141 6.72218 4.76418 6.72218 3.78075C6.72218 2.79732 5.92624 2.00009 4.9444 2.00009C3.96256 2.00009 3.16663 2.79732 3.16663 3.78075C3.16663 4.76418 3.96256 5.56141 4.9444 5.56141ZM4.9444 5.56141V13.3779M12.0555 9.81655C13.0374 9.81655 13.8333 10.6138 13.8333 11.5972C13.8333 12.5806 13.0374 13.3779 12.0555 13.3779C11.0737 13.3779 10.2777 12.5806 10.2777 11.5972C10.2777 10.6138 11.0737 9.81655 12.0555 9.81655ZM12.0555 9.81655V5.58514C12.0555 4.6033 11.2596 3.80736 10.2777 3.80736H9.21107"
                          stroke="#6B6D6E"
                          strokeWidth="1.33333"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="">{item.github.pr}</p>
                    </div>
                    <div className="flex items-center relative gap-1">
                      <svg
                        width={17}
                        height={16}
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 relative"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M10.6909 7.99999H14.9M2.10003 7.88196H6.70801M6.70425 7.99999C6.70425 9.00651 7.5202 9.82246 8.52672 9.82246C9.53325 9.82246 10.3492 9.00651 10.3492 7.99999C10.3492 6.99347 9.53325 6.17752 8.52672 6.17752C7.5202 6.17752 6.70425 6.99347 6.70425 7.99999Z"
                          stroke="#6B6D6E"
                          strokeWidth="1.33333"
                          strokeLinecap="round"
                        />
                      </svg>
                      <p className="">{item.github.commits}</p>
                    </div>
                    <div className="flex items-center relative gap-1">
                      <svg
                        width={17}
                        height={16}
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 relative"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M8.49996 2.60132L10.148 5.93999L13.8333 6.47865L11.1666 9.07599L11.796 12.7453L8.49996 11.012L5.20396 12.7453L5.83329 9.07599L3.16663 6.47865L6.85196 5.93999L8.49996 2.60132Z"
                          stroke="#6B6D6E"
                          strokeWidth="1.33333"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="">{item.github.stars}</p>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}